import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SolutionsSection = () => {
  const [activeLoB, setActiveLoB] = useState('OTC');
  const navigate = useNavigate();

  const linesOfBusiness = [
    {
      id: 'OTC',
      name: 'Order to Cash',
      description: 'Sales order processing, delivery, and billing workflows',
      icon: 'ShoppingCart',
      color: 'bg-blue-500',
      practices: [
        { code: '1QM', title: 'Quality Management in Sales', version: '2.0', complexity: 'Medium' },
        { code: '5D2', title: 'Sales Order Processing', version: '1.8', complexity: 'High' },
        { code: '2LX', title: 'Customer Returns Management', version: '1.5', complexity: 'Low' },
        { code: '8K3', title: 'Credit Management', version: '2.1', complexity: 'Medium' }
      ]
    },
    {
      id: 'P2P',
      name: 'Procure to Pay',
      description: 'Procurement, purchasing, and vendor payment processes',
      icon: 'Package',
      color: 'bg-green-500',
      practices: [
        { code: '3PR', title: 'Purchase Requisition Processing', version: '1.9', complexity: 'Medium' },
        { code: '4PO', title: 'Purchase Order Management', version: '2.0', complexity: 'High' },
        { code: '6IV', title: 'Invoice Verification', version: '1.7', complexity: 'Low' },
        { code: '7VM', title: 'Vendor Master Management', version: '1.6', complexity: 'Medium' }
      ]
    },
    {
      id: 'RTR',
      name: 'Record to Report',
      description: 'Financial accounting, reporting, and compliance processes',
      icon: 'FileBarChart',
      color: 'bg-purple-500',
      practices: [
        { code: '9GL', title: 'General Ledger Accounting', version: '2.2', complexity: 'High' },
        { code: '1AR', title: 'Accounts Receivable', version: '1.8', complexity: 'Medium' },
        { code: '2AP', title: 'Accounts Payable', version: '1.9', complexity: 'Medium' },
        { code: '3FC', title: 'Financial Closing', version: '2.0', complexity: 'High' }
      ]
    },
    {
      id: 'Plan',
      name: 'Plan to Produce',
      description: 'Production planning, manufacturing, and supply chain',
      icon: 'Factory',
      color: 'bg-orange-500',
      practices: [
        { code: '4PP', title: 'Production Planning', version: '1.7', complexity: 'High' },
        { code: '5MO', title: 'Manufacturing Orders', version: '1.6', complexity: 'Medium' },
        { code: '6SC', title: 'Supply Chain Planning', version: '2.1', complexity: 'High' },
        { code: '7QC', title: 'Quality Control', version: '1.5', complexity: 'Low' }
      ]
    },
    {
      id: 'EWM',
      name: 'Extended Warehouse',
      description: 'Advanced warehouse management and logistics',
      icon: 'Warehouse',
      color: 'bg-accent',
      practices: [
        { code: '8WM', title: 'Warehouse Management', version: '2.0', complexity: 'High' },
        { code: '9IN', title: 'Inbound Processing', version: '1.8', complexity: 'Medium' },
        { code: '1OB', title: 'Outbound Processing', version: '1.9', complexity: 'Medium' },
        { code: '2IV', title: 'Inventory Management', version: '1.7', complexity: 'Low' }
      ]
    }
  ];

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'Low': return 'text-green-600 bg-green-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'High': return 'text-red-600 bg-red-100';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const handleStartEngagement = () => {
    navigate('/user-registration');
  };

  const handleExplorePractices = () => {
    navigate('/best-practice-picker');
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-accent/10 border border-accent/20 rounded-full text-accent text-sm font-medium mb-6">
            <Icon name="Building2" size={16} className="mr-2" />
            Solutions by Line of Business
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            SAP Best Practices Library
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Explore our comprehensive collection of SAP Best Practices organized by business function. 
            Each practice includes detailed scope, configuration guides, and implementation templates.
          </p>
        </div>

        {/* LoB Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {linesOfBusiness?.map((lob) => (
            <button
              key={lob?.id}
              onClick={() => setActiveLoB(lob?.id)}
              className={`flex items-center space-x-3 px-6 py-3 rounded-lg border transition-all duration-200 ${
                activeLoB === lob?.id
                  ? 'bg-primary text-primary-foreground border-primary shadow-elevation-1'
                  : 'bg-card text-foreground border-border hover:border-primary/50 hover:shadow-elevation-1'
              }`}
            >
              <div className={`w-8 h-8 ${activeLoB === lob?.id ? 'bg-primary-foreground/20' : lob?.color} rounded-lg flex items-center justify-center`}>
                <Icon 
                  name={lob?.icon} 
                  size={16} 
                  className={activeLoB === lob?.id ? 'text-primary-foreground' : 'text-white'}
                />
              </div>
              <div className="text-left">
                <div className="font-medium">{lob?.name}</div>
                <div className={`text-xs ${activeLoB === lob?.id ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  {lob?.id}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Active LoB Content */}
        {linesOfBusiness?.map((lob) => (
          activeLoB === lob?.id && (
            <div key={lob?.id} className="space-y-8">
              {/* LoB Overview */}
              <div className="bg-card border border-border rounded-xl p-8">
                <div className="flex items-start space-x-6">
                  <div className={`w-16 h-16 ${lob?.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon name={lob?.icon} size={32} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {lob?.name} ({lob?.id})
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {lob?.description}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        variant="default"
                        onClick={handleStartEngagement}
                        iconName="Play"
                        iconPosition="left"
                      >
                        Start an Engagement
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleExplorePractices}
                        iconName="Search"
                        iconPosition="left"
                      >
                        Explore All Practices
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Best Practices Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {lob?.practices?.map((practice) => (
                  <div key={practice?.code} className="bg-card border border-border rounded-lg p-6 hover-lift hover:shadow-elevation-2 transition-all duration-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-bold text-primary">
                            {practice?.code}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground">
                            {practice?.title}
                          </h4>
                          <div className="text-sm text-muted-foreground">
                            Version {practice?.version}
                          </div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(practice?.complexity)}`}>
                        {practice?.complexity}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="FileText" size={14} />
                          <span>BPD² Ready</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Settings" size={14} />
                          <span>Config Guide</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="ArrowRight"
                        iconPosition="right"
                      >
                        Select
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    </section>
  );
};

export default SolutionsSection;